import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, Globe } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ darkMode, toggleDarkMode, language, toggleLanguage }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const translations = {
    de: {
      home: 'Startseite',
      prices: 'Preise',
      privateServices: 'Privatkunden',
      businessServices: 'Unternehmen',
      about: 'Über mich',
      privacy: 'Datenschutz',
      imprint: 'Impressum'
    },
    en: {
      home: 'Home',
      prices: 'Prices',
      privateServices: 'Private Customers',
      businessServices: 'Businesses',
      about: 'About',
      privacy: 'Privacy',
      imprint: 'Imprint'
    }
  };

  const t = translations[language];

  const navItems = [
    { path: '/', label: t.home },
    { path: '/prices', label: t.prices },
    { path: '/private-services', label: t.privateServices },
    { path: '/business-services', label: t.businessServices },
    { path: '/about', label: t.about },
    { path: '/privacy', label: t.privacy },
    { path: '/imprint', label: t.imprint }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 shrink-0 mr-4">
            <img
              src="/images/website_logo.png"
              alt="Martinez IT Systems Logo"
              className="site-logo"
            />
          </Link>

          {/* Desktop Navigation - Hidden on screens smaller than 1100px to prioritize logo visibility */}
          <nav className="hidden xl:flex items-center space-x-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors hover:text-[#E53D4A] whitespace-nowrap ${
                  isActive(item.path)
                    ? 'text-[#E53D4A] font-semibold'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Controls */}
          <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="hidden sm:flex shrink-0"
              aria-label="Toggle language"
            >
              <Globe className="h-5 w-5" />
              <span className="ml-1 text-xs font-medium">{language.toUpperCase()}</span>
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              className="shrink-0"
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {/* Mobile Menu Button - Show on screens smaller than 1280px */}
            <Button
              variant="ghost"
              size="icon"
              className="xl:hidden shrink-0"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation - Show on screens smaller than 1280px */}
        {menuOpen && (
          <nav className="xl:hidden py-4 space-y-2 border-t dark:border-gray-800">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={`block px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-gray-100 dark:bg-gray-800 text-[#E53D4A]'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="w-full justify-start sm:hidden"
            >
              <Globe className="h-4 w-4 mr-2" />
              {language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
